// Context for global autocomplete setting
// Allows toggling suggestions on/off across all math editors

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface AutocompleteContextType {
    autocompleteEnabled: boolean;
    setAutocompleteEnabled: (enabled: boolean) => void;
    hintsEnabled: boolean;
    setHintsEnabled: (enabled: boolean) => void;
}

const AutocompleteContext = createContext<AutocompleteContextType | null>(null);

const STORAGE_KEY = "seetex-autocomplete-enabled";
const HINTS_KEY = "seetex-hints-enabled";

export function AutocompleteProvider({ children }: { children: ReactNode }) {
    const [autocompleteEnabled, setAutocompleteEnabled] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored !== null ? stored === "true" : true;
    });

    const [hintsEnabled, setHintsEnabled] = useState(() => {
        const stored = localStorage.getItem(HINTS_KEY);
        return stored !== null ? stored === "true" : true;
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, String(autocompleteEnabled));
    }, [autocompleteEnabled]);

    useEffect(() => {
        localStorage.setItem(HINTS_KEY, String(hintsEnabled));
    }, [hintsEnabled]);

    return (
        <AutocompleteContext.Provider value={{ autocompleteEnabled, setAutocompleteEnabled, hintsEnabled, setHintsEnabled }}>
            {children}
        </AutocompleteContext.Provider>
    );
}

export function useAutocomplete(): AutocompleteContextType {
    const ctx = useContext(AutocompleteContext);
    if (!ctx) {
        throw new Error("useAutocomplete must be used within AutocompleteProvider");
    }
    return ctx;
}
